/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react';
import { useTheme, useMediaQuery, Grid, Typography } from '@material-ui/core';
import { Close as CloseIcon, Print as PrintIcon } from '@material-ui/icons';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import QCXIconButton from '../../shared-components/button/QCXIconButton';
import QCXDataGrid from '../../shared-components/data-grid/QCXDataGrid';
import QCXCompoundDialog from '../../shared-components/dialog/QCXCompoundDialog';
import { CLOSED_STATUS, isOpenStatus } from '../../utils/hooks/form/dialog/formDialogUtils';

import useDownloader from '../../utils/hooks/downloader/useDownloader';
import { isLoadingStatus } from '../../utils/store/store-utils';

export default function QCXDocumentosSPDialogManager({ model, status, handleStatus, ...restProps }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const downloader = useDownloader({ autoDownload: true });
  const downloading = useMemo(() => isLoadingStatus(downloader.status), [downloader]);

  const documentoArquivoList = useMemo(() => model?.documentos || [], [model]);

  const open = useMemo(() => isOpenStatus(status), [status]);

  const handleClose = useCallback(() => {
    handleStatus(CLOSED_STATUS);
  }, [handleStatus]);

  const isMultipleSelection = useMemo(() => selectedDocuments.length > 1, [selectedDocuments]);

  const handleSelectionDocumentsChange = useCallback(
    ({ selectionModel }) => {
      setSelectedDocuments(selectionModel);
    },
    []
  );

  const configureDownloadSingleDocumentHandlerBy = useCallback(
    ({ id }) => {
      const handle = (event) => {
        if (_.isFunction(event?.stopPropagation)) event.stopPropagation();
        downloader.downloadFileById(id);
      };
      return handle;
    },
    [downloader]
  );

  const columns = useMemo(
    () => [
      {
        field: 'nome',
        headerName: 'Documentos',
        flex: 200,
        valueGetter: ({ row }) => row?.nome || '-',
      },
      {
        field: 'tipoDocumento',
        headerName: 'Tipo de Documento',
        flex: 150,
        valueGetter: ({ row }) => (row?.extensao || '-').toUpperCase(),
      },
      {
        field: 'actions',
        headerName: t('com.muralis.qcx.acoes.label'),
        headerAlign: 'center',
        align: 'center',
        flex: 110,
        renderCell: ({ row }) => (
          <>

            <QCXIconButton
              key={`icon-button-documento-file-download-${row?.id}`}
              name={`icon-button-documento-file-download-${row?.id}`}
              tooltipProps={{ title: t('com.muralis.qcx.documento.baixarDocumento') }}
              onClick={configureDownloadSingleDocumentHandlerBy({ id: row?.id })}
              disableTooltip={isMultipleSelection}
              disabled={isMultipleSelection || downloading}
            >
              <FileDownloadIcon
                htmlColor={!isMultipleSelection ? theme.palette.primary.main : '#00000042'}
                size={20}
              />
            </QCXIconButton>
          </>
        ),
      },
    ],
    [t, theme, isMultipleSelection, downloading, configureDownloadSingleDocumentHandlerBy]
  );

  return (
    <QCXCompoundDialog
      key="dialog-manager-sp"
      id="dialog-manager-sp"
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableBackdropClick
      {...restProps}
    >
      <QCXCompoundDialog.Title style={{ padding: '16px 32px 16px 24px' }}>
        <Grid item container justify="space-between">
          <Grid item xs={10}>
            <Typography variant="subtitle2" style={{ color: theme.palette.primary.light }}>
              <strong>
                {t('com.muralis.qcx.documento.documentoAdicionadosProcessoNumero', {
                  numero: model?.numero || t('com.muralis.qcx.solicitacaoPagamento.label', 'Solicitação de Pagamento'),
                }).toUpperCase()}
              </strong>
            </Typography>
          </Grid>
          <Grid item container justify="flex-end" xs={2}>
            <QCXIconButton
              style={{ margin: '-16px -32px 0 0', padding: '10px', borderRadius: '4px' }}
              onClick={handleClose}
              disableTooltip
              disableRipple
            >
              <CloseIcon />
            </QCXIconButton>
          </Grid>
        </Grid>
      </QCXCompoundDialog.Title>

      <QCXCompoundDialog.Content style={{ padding: '16px 32px 16px 24px' }}>
        <QCXDataGrid
          columns={columns}
          rows={documentoArquivoList}
          selectionModel={selectedDocuments}
          onSelectionModelChange={handleSelectionDocumentsChange}
        />
      </QCXCompoundDialog.Content>
    </QCXCompoundDialog>
  );
}
